import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EChart } from "@/components/charts/echart";
import { chartColors, powerPalette, triPhasePalette } from "@/components/charts/palette";
import type { TelemetryPoint } from "@/lib/api";

type TelemetryChartGridProps = {
  loading: boolean;
  points: TelemetryPoint[] | undefined;
};

type ChartOption = Parameters<typeof EChart>[0]["option"];

export function TelemetryChartGrid({ loading, points }: TelemetryChartGridProps): JSX.Element {
  const chronological = [...(points ?? [])].reverse();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard
        title="Line voltages"
        loading={loading}
        points={chronological}
        option={buildVoltageOption(chronological)}
      />
      <ChartCard
        title="Phase currents"
        loading={loading}
        points={chronological}
        option={buildCurrentOption(chronological)}
      />
      <ChartCard
        title="Power"
        loading={loading}
        points={chronological}
        option={buildPowerOption(chronological)}
      />
      <ChartCard
        title="Power factor & frequency"
        loading={loading}
        points={chronological}
        option={buildPfFreqOption(chronological)}
      />
    </div>
  );
}

function ChartCard({
  title,
  loading,
  points,
  option,
}: {
  title: string;
  loading: boolean;
  points: TelemetryPoint[];
  option: ChartOption;
}): JSX.Element {
  const isEmpty = !loading && points.length === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[280px] w-full" />
        ) : isEmpty ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
            No telemetry samples yet.
          </div>
        ) : (
          <EChart option={option} height={280} />
        )}
      </CardContent>
    </Card>
  );
}

function buildVoltageOption(points: TelemetryPoint[]): ChartOption {
  return {
    grid: chartGrid(),
    legend: { data: ["V L1-L2", "V L2-L3", "V L3-L1"], top: 0 },
    tooltip: { trigger: "axis" as const, valueFormatter: (value) => formatChartValue(value, " V") },
    dataZoom: chartZoom(),
    xAxis: timeAxis(),
    yAxis: valueAxis("V"),
    series: [
      {
        name: "V L1-L2",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.v_l12_x100, 100)]),
        itemStyle: { color: triPhasePalette.l12 },
      },
      {
        name: "V L2-L3",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.v_l23_x100, 100)]),
        itemStyle: { color: triPhasePalette.l23 },
      },
      {
        name: "V L3-L1",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.v_l31_x100, 100)]),
        itemStyle: { color: triPhasePalette.l31 },
      },
    ],
  };
}

function buildCurrentOption(points: TelemetryPoint[]): ChartOption {
  return {
    grid: chartGrid(),
    legend: { data: ["I L1", "I L2", "I L3"], top: 0 },
    tooltip: { trigger: "axis" as const, valueFormatter: (value) => formatChartValue(value, " A") },
    dataZoom: chartZoom(),
    xAxis: timeAxis(),
    yAxis: valueAxis("A"),
    series: [
      {
        name: "I L1",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.a_l1_x100, 100)]),
        itemStyle: { color: triPhasePalette.l12 },
      },
      {
        name: "I L2",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.a_l2_x100, 100)]),
        itemStyle: { color: triPhasePalette.l23 },
      },
      {
        name: "I L3",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.a_l3_x100, 100)]),
        itemStyle: { color: triPhasePalette.l31 },
      },
    ],
  };
}

function buildPowerOption(points: TelemetryPoint[]): ChartOption {
  return {
    grid: chartGrid(),
    legend: { data: ["Active", "Reactive", "Apparent"], top: 0 },
    tooltip: { trigger: "axis" as const, valueFormatter: (value) => formatChartValue(value, " W") },
    dataZoom: chartZoom(),
    xAxis: timeAxis(),
    yAxis: valueAxis("W / VAR / VA"),
    series: [
      {
        name: "Active",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        areaStyle: { opacity: 0.18 },
        data: points.map((p) => [p.ingested_at, scaled(p.active_power_inst_x10, 10)]),
        itemStyle: { color: powerPalette.active },
      },
      {
        name: "Reactive",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.reactive_power_inst_x10, 10)]),
        itemStyle: { color: powerPalette.reactive },
      },
      {
        name: "Apparent",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        data: points.map((p) => [p.ingested_at, scaled(p.apparent_power_inst_x10, 10)]),
        itemStyle: { color: powerPalette.apparent },
      },
    ],
  };
}

function buildPfFreqOption(points: TelemetryPoint[]): ChartOption {
  return {
    grid: chartGrid(),
    legend: { data: ["Power factor", "Frequency"], top: 0 },
    tooltip: { trigger: "axis" as const },
    dataZoom: chartZoom(),
    xAxis: timeAxis(),
    yAxis: [
      { type: "value" as const, name: "PF", min: 0, max: 1 },
      { type: "value" as const, name: "Hz" },
    ],
    series: [
      {
        name: "Power factor",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        yAxisIndex: 0,
        data: points.map((p) => [p.ingested_at, scaled(p.power_factor_x1000, 1000)]),
        itemStyle: { color: chartColors.red },
      },
      {
        name: "Frequency",
        type: "line",
        smooth: false,
        showSymbol: false,
        sampling: "lttb",
        lineStyle: chartLineStyle(),
        emphasis: chartEmphasis(),
        yAxisIndex: 1,
        data: points.map((p) => [p.ingested_at, scaled(p.frequency_x100, 100)]),
        itemStyle: { color: chartColors.blue },
      },
    ],
  };
}

function scaled(value: number | null, divisor: number): number | null {
  if (value === null || value === undefined) return null;
  return Number((value / divisor).toFixed(3));
}

function chartGrid() {
  return { top: 48, right: 40, bottom: 50, left: 64 };
}

function timeAxis() {
  return { type: "time" as const, boundaryGap: false };
}

function valueAxis(name: string) {
  return { type: "value" as const, name, scale: true };
}

function chartZoom() {
  return [
    { type: "inside" as const, filterMode: "none" as const },
    { type: "slider" as const, height: 18, bottom: 12, borderColor: "transparent" },
  ];
}

function chartLineStyle() {
  return { width: 2 };
}

function chartEmphasis() {
  return { focus: "series" as const };
}

function formatChartValue(value: unknown, suffix: string): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "--";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}
